import { Box, Typography } from "@mui/material";
import type { SxProps, Theme } from "@mui/material/styles";
import type { FC, ReactElement, ReactNode } from "react";
import { HEATMAP_SEVERITY } from "../../../../theme/tokens";
import { InHouseTooltip } from "../../../InHouseTooltip/InHouseTooltip";
import type { PerformanceHeatmapCell, PerformanceHeatmapColumn, PerformanceHeatmapRow, PerformanceHeatmapTone } from "./types";

const toneStyles: Record<PerformanceHeatmapTone, { bgcolor: string; color: string }> = {
	success: HEATMAP_SEVERITY.SUCCESS,
	watch: HEATMAP_SEVERITY.WATCH,
	caution: HEATMAP_SEVERITY.CAUTION,
	elevated: HEATMAP_SEVERITY.ELEVATED,
	high: HEATMAP_SEVERITY.HIGH,
	alert: HEATMAP_SEVERITY.ALERT,
	danger: HEATMAP_SEVERITY.DANGER,
	critical: HEATMAP_SEVERITY.CRITICAL,
	severe: HEATMAP_SEVERITY.SEVERE,
	neutral: { bgcolor: "action.hover", color: "text.secondary" },
};

type Props = {
	columns: PerformanceHeatmapColumn[];
	rows: PerformanceHeatmapRow[];
	ariaLabel: string;
	rowHeaderLabel: string;
	emptyState?: ReactNode;
	stickyHeader?: boolean;
	stickyFirstColumn?: boolean;
	dataTestId: string;
	sx?: SxProps<Theme>;
};

const tooltipCell = (cell: PerformanceHeatmapCell, children: ReactElement): ReactElement => {
	if (cell.tooltip === undefined) return children;
	return (
		<InHouseTooltip key={cell.dataTestId} title={cell.tooltip} arrow placement="top">
			{children}
		</InHouseTooltip>
	);
};

/**
 * Generic, semantic performance matrix for compact actual-vs-reference scans. Callers supply all
 * domain labels, formatting, status decisions, tooltips, and empty state; this component owns only
 * accessible table geometry, tokenized tones, optional sticky axes, and keyboard tooltip access.
 */
export const InHousePerformanceHeatmap: FC<Props> = ({
	columns,
	rows,
	ariaLabel,
	rowHeaderLabel,
	emptyState,
	stickyHeader,
	stickyFirstColumn,
	dataTestId,
	sx,
}) => (
	<Box data-testid={dataTestId} sx={{ width: "100%", overflowX: "auto" }}>
		<Box
			component="table"
			aria-label={ariaLabel}
			sx={[{ width: "100%", borderCollapse: "collapse", fontVariantNumeric: "tabular-nums" }, ...(Array.isArray(sx) ? sx : [sx])]}
		>
			<Box component="thead">
				<Box component="tr">
					<Box
						component="th"
						scope="col"
						aria-label={rowHeaderLabel}
						sx={{
							px: 1.5,
							py: 1,
							textAlign: "start",
							bgcolor: "background.default",
							border: 1,
							borderColor: "divider",
							whiteSpace: "nowrap",
							...(stickyHeader === true ? { position: "sticky", top: 0, zIndex: "appBar" } : {}),
							...(stickyFirstColumn === true ? { left: 0, zIndex: "tooltip" } : {}),
						}}
					>
						<Typography component="span" variant="caption" sx={{ fontWeight: 700, color: "text.secondary" }}>
							{rowHeaderLabel}
						</Typography>
					</Box>
					{columns.map((column) => (
						<Box
							component="th"
							key={column.id}
							scope="col"
							aria-label={column.ariaLabel}
							sx={{
								px: 1,
								py: 1,
								textAlign: "center",
								bgcolor: "background.default",
								border: 1,
								borderColor: "divider",
								whiteSpace: "nowrap",
								...(stickyHeader === true ? { position: "sticky", top: 0, zIndex: "appBar" } : {}),
							}}
						>
							<Typography variant="caption" sx={{ fontWeight: 700, color: "text.secondary" }}>
								{column.label}
							</Typography>
						</Box>
					))}
				</Box>
			</Box>
			<Box component="tbody">
				{rows.length === 0 && emptyState !== undefined && (
					<Box component="tr">
						<Box component="td" colSpan={columns.length + 1} sx={{ border: 1, borderColor: "divider" }}>
							{emptyState}
						</Box>
					</Box>
				)}
				{rows.map((row) => (
					<Box component="tr" key={row.id}>
						<Box
							component="th"
							scope="row"
							aria-label={row.ariaLabel}
							sx={{
								px: 1.5,
								py: 1.25,
								textAlign: "start",
								bgcolor: "background.paper",
								border: 1,
								borderColor: "divider",
								whiteSpace: "nowrap",
								...(stickyFirstColumn === true ? { position: "sticky", left: 0, zIndex: "mobileStepper" } : {}),
							}}
						>
							<Typography noWrap variant="body2" sx={{ fontWeight: 600 }}>
								{row.label}
							</Typography>
						</Box>
						{columns.map((column) => {
							const cell = row.cells[column.id];
							if (cell === undefined) throw new Error(`Performance heatmap row ${row.id} has no cell for column ${column.id}`);
							return tooltipCell(
								cell,
								<Box
									component="td"
									key={column.id}
									tabIndex={cell.tooltip === undefined ? undefined : 0}
									aria-label={cell.ariaLabel}
									data-testid={cell.dataTestId}
									sx={{
										px: 1,
										py: 1.25,
										textAlign: "center",
										border: 1,
										borderColor: "divider",
										whiteSpace: "nowrap",
										...toneStyles[cell.tone],
										"@media (hover: hover) and (pointer: fine)": { "&:hover": { filter: "brightness(0.97)" } },
										"&:focus-visible": { outline: "thin solid", outlineColor: "primary.main" },
									}}
								>
									<Typography variant="body2" sx={{ fontWeight: 700, color: "inherit" }}>
										{cell.value}
									</Typography>
								</Box>,
							);
						})}
					</Box>
				))}
			</Box>
		</Box>
	</Box>
);
